import { toast } from "react-toastify";
import * as XLSX from "xlsx";

const validateImport = (rows: Array<any>) => {
  if (rows.length === 0) {
    const message = "excel sheet has no rows";
    toast.error(message);
    return false;
  }

  for (let i = 0; i < rows.length; i += 1) {
    const row = rows[i] as Array<any>;
    const rowNumber = i + 1;

    const hasEmptyColumns =
      row
        // first colum is Network which can be empty
        .slice(1)
        .filter((x) => x === undefined || x === null || x === "").length > 0;
    if (hasEmptyColumns) {
      const message = `Unable to import excel sheet. Found empty columns in row ${rowNumber}`;
      toast.error(message);
      return false;
    }

    const columnSize = 9;
    if (row.length > columnSize) {
      const message = `Unable to import excel sheet. Found too many (${
        row.length + 1
      }) columns in row ${rowNumber}. Expected to have ${columnSize}`;
      toast.error(message);
      return false;
    }
  }

  return true;
};

function getJsonFromExcel(wb: XLSX.WorkBook) {
  if (wb.SheetNames.length > 1) {
    throw new Error("excel has more than one sheet. This is unexpected");
  }

  const jsonExcel: Array<any> = [];
  wb.SheetNames.map((sheetName: string) => {
    const sheet = wb.Sheets[sheetName];
    const jsonItems = XLSX.utils.sheet_to_json(sheet, { header: 1 });
    return (
      jsonItems
        // skip first row since it's header
        .slice(1)
    );
  }).forEach((items) => jsonExcel.push(...items));

  return jsonExcel.filter((x) => x.length !== 0);
}

export type VolumeDataType = {
  Network: string;
  Col1: string;
  Col2: string;
  Col3: string;
  MLDM: number;
  ELDM: number;
  KG: number;
  M3: number;
  EngineeredSf: number;
};

function parseVolumeData(jsonRows: Array<any>): Array<VolumeDataType> {
  return jsonRows.map((row) => {
    return {
      Network: row[0],
      Col1: row[1],
      Col2: row[2],
      Col3: row[3],
      MLDM: row[4],
      ELDM: row[5],
      KG: row[6],
      M3: row[7],
      EngineeredSf: row[8],
    } as VolumeDataType;
  });
}

function readFileAsync(file: Blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result);
    };

    reader.onerror = reject;

    reader.readAsArrayBuffer(file);
  });
}

export const readVolumeDataFile = async (file: File) => {
  const fileData = await readFileAsync(file);

  const wb = XLSX.read(fileData, { type: "binary" });
  const jsonRows = getJsonFromExcel(wb);

  const isExcelValid = validateImport(jsonRows);
  if (!isExcelValid) {
    const message = "The given Excel file is not valid!";
    toast.error(message);
    return [];
  }

  return parseVolumeData(jsonRows);
};
